import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { Input } from 'components/Shared';
import { useValidation } from 'hooks';
import ReCAPTCHA from 'react-google-recaptcha';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from 'store';
import { LoginActions as MainActions } from 'store/ducks/auth';
import * as Yup from 'yup';
import * as S from './styles';

const RECAPTCHA_KEY = process.env.REACT_APP_RECAPTCHA_KEY as string;

function formatCountdown(totalSeconds: number): string {
  if (totalSeconds >= 3600) {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    return `${hours}h${minutes.toString().padStart(2, '0')}min`;
  }
  if (totalSeconds >= 60) {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}min ${seconds.toString().padStart(2, '0')}s`;
  }
  return `${totalSeconds}s`;
}

export const LoginForm: React.FC = () => {
  const [human, setHuman] = useState<string | boolean>(false);
  const [blockedSeconds, setBlockedSeconds] = useState(0);
  const dispatch: AppDispatch = useDispatch();
  const formRef = useRef<FormHandles>(null);
  const { handleFormErrors, handleApiErrors } = useValidation();

  const onRecaptcha = useCallback((value: any) => {
    setHuman(value);
  }, []);

  const { loading, validationErrors, lockoutSeconds } = useSelector(
    (state: RootState) => state.login
  );

  const isBlocked = blockedSeconds > 0;

  useEffect(() => {
    if (lockoutSeconds > 0) {
      setBlockedSeconds(lockoutSeconds);
    }
  }, [lockoutSeconds]);

  useEffect(() => {
    if (blockedSeconds <= 0) return;
    const timer = setInterval(() => {
      setBlockedSeconds((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [blockedSeconds]);

  const onFailure = (messageError: string, retryAfter?: number) => {
    formRef.current?.setFieldError('password', messageError);
    if (retryAfter && retryAfter > 0) {
      setBlockedSeconds(retryAfter);
    }
  };

  const onSubmit = useCallback(
    async (data: any): Promise<void> => {
      if (isBlocked) return;

      try {
        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          email: Yup.string()
            .email('E-mail inválido')
            .required('E-mail obrigatório'),
          password: Yup.string().required('Senha obrigatória'),
        });
        const validData = await schema.validate(
          {
            ...data,
            humankey: human,
          },
          {
            abortEarly: false,
          }
        );

        dispatch(MainActions.request(validData, null, onFailure));
      } catch (error) {
        handleFormErrors(error, formRef);
      }
    },
    [dispatch, handleFormErrors, human, isBlocked]
  );

  useEffect(() => {
    handleApiErrors(validationErrors, formRef);
  }, [handleApiErrors, validationErrors]);

  useEffect(() => {
    return () => {
      dispatch(MainActions.reset());
    };
  }, [dispatch]);

  return (
    <S.Container>
      <Form ref={formRef} onSubmit={human? onSubmit:()=>{}}>
        <Input name="email" label="E-mail" disabled={isBlocked} />
        <Input name="password" label="Senha" type="password" disabled={isBlocked} />
        <S.ReCaptchaContainer>
          <ReCAPTCHA sitekey={RECAPTCHA_KEY} onChange={onRecaptcha} />
        </S.ReCaptchaContainer>
        {isBlocked ? (
          <S.LockoutMessage>
            Nova tentativa disponível em {formatCountdown(blockedSeconds)}.
          </S.LockoutMessage>
        ) : null}
        <S.FormActions>
          <S.Button type="submit" disabled={!human || isBlocked}>
            {loading ? (
              <S.ActivityIndicator />
            ) : isBlocked ? (
              `Aguarde ${formatCountdown(blockedSeconds)}`
            ) : (
              'Entrar'
            )}
          </S.Button>
          <S.ForgotMyPwsLink to="/esqueci-minha-senha">
            Esqueceu sua senha?
          </S.ForgotMyPwsLink>
        </S.FormActions>
      </Form>
    </S.Container>
  );
};
